import { lazy, Suspense } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { CartProvider } from "@/contexts/CartContext";
import { WishlistProvider } from "@/contexts/WishlistContext";
import { AuthProvider } from "@/contexts/AuthContext";
import { AddressProvider } from "@/contexts/AddressContext";
import { BraceletGiftProvider } from "@/contexts/BraceletGiftContext";
import { AdminRoute } from "@/components/AdminRoute";
import { PageLoading } from "@/components/PageLoading";
import { RouteErrorBoundary } from "@/components/RouteErrorBoundary";
import { RouteMetadata } from "@/components/RouteMetadata";
import { AnalyticsChoice } from "@/components/AnalyticsChoice";

// The storefront home page stays in the entry chunk — it is the LCP route, so
// splitting it would add a round trip before the hero can even be requested.
import Index from "./pages/Index";

const Cart = lazy(() => import("./pages/Cart"));
const Auth = lazy(() => import("./pages/Auth"));
const Checkout = lazy(() => import("./pages/Checkout"));
const OrderSuccess = lazy(() => import("./pages/OrderSuccess"));
const Orders = lazy(() => import("./pages/Orders"));
const OrderDetails = lazy(() => import("./pages/OrderDetails"));
const ProductDetail = lazy(() => import("./pages/ProductDetail"));
const CategoryPage = lazy(() => import("./pages/CategoryPage"));
const Gemfinder = lazy(() => import("./pages/Gemfinder"));
const Wishlist = lazy(() => import("./pages/Wishlist"));
const NotFound = lazy(() => import("./pages/NotFound"));
const TermsConditions = lazy(() => import("./pages/TermsConditions"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const ShippingPolicy = lazy(() => import("./pages/ShippingPolicy"));
const ContactUs = lazy(() => import("./pages/ContactUs"));
const CancellationRefunds = lazy(() => import("./pages/CancellationRefunds"));
const Profile = lazy(() => import("./pages/Profile"));
const Blogs = lazy(() => import("./pages/Blogs"));
const BlogDetail = lazy(() => import("./pages/BlogDetail"));
const VarahiNavaratrulu = lazy(() => import("./pages/campaigns/VarahiNavaratrulu"));

const AdminDashboard = lazy(() => import("./pages/admin/AdminDashboard"));
const AdminOrders = lazy(() => import("./pages/admin/AdminOrders"));
const AdminOrderDetails = lazy(() => import("./pages/admin/AdminOrderDetails"));
const AdminProducts = lazy(() => import("./pages/admin/AdminProducts"));
const AdminBestsellers = lazy(() => import("./pages/admin/AdminBestsellers"));
const AdminBlogs = lazy(() => import("./pages/admin/AdminBlogs"));
const AdminBanners = lazy(() => import("./pages/admin/AdminBanners"));
const AdminReviews = lazy(() => import("./pages/admin/AdminReviews"));
const AdminCoupons = lazy(() => import("./pages/admin/AdminCoupons"));
const AdminOffers = lazy(() => import("./pages/admin/AdminOffers"));
const AdminBraceletGift = lazy(() => import("./pages/admin/AdminBraceletGift"));
const AdminRecentPurchases = lazy(() => import("./pages/admin/AdminRecentPurchases"));
const AdminGemfinder = lazy(() => import("./pages/admin/AdminGemfinder"));
const AdminAppPromotion = lazy(() => import("./pages/admin/AdminAppPromotion"));

const queryClient = new QueryClient();

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <AuthProvider>
        <AddressProvider>
          <WishlistProvider>
            <CartProvider>
              <BraceletGiftProvider>
              <Toaster />
              <Sonner />
              <AnalyticsChoice />
              <BrowserRouter future={{ v7_relativeSplatPath: true }}>
                <RouteMetadata />
                <RouteErrorBoundary>
                  <Suspense fallback={<PageLoading />}>
                  <Routes>
                  <Route path="/" element={<Index />} />
                  <Route path="/category/:categorySlug" element={<CategoryPage />} />
                  <Route path="/gemfinder" element={<Gemfinder />} />
                  <Route path="/product/:categorySlug/:productSlug" element={<ProductDetail />} />
                  <Route path="/product/:productId" element={<ProductDetail />} />
                  <Route path="/cart" element={<Cart />} />
                  <Route path="/wishlist" element={<Wishlist />} />
                  <Route path="/auth" element={<Auth />} />
                  <Route path="/checkout" element={<Checkout />} />
                  <Route path="/order-success" element={<OrderSuccess />} />
                  {import.meta.env.DEV && <Route path="/dev/payment-success" element={<OrderSuccess preview />} />}
                  <Route path="/orders" element={<Orders />} />
                  <Route path="/profile" element={<Profile />} />
                  <Route path="/order/:orderId" element={<OrderDetails />} />
                  <Route path="/terms-conditions" element={<TermsConditions />} />
                  <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                  <Route path="/shipping-policy" element={<ShippingPolicy />} />
                  <Route path="/contact-us" element={<ContactUs />} />
                  <Route path="/cancellation-refunds" element={<CancellationRefunds />} />
                  <Route path="/blog" element={<Blogs />} />
                  <Route path="/blog/index.html" element={<Blogs />} />
                  <Route path="/blog/:language/:slug/:blogId" element={<BlogDetail />} />
                  <Route path="/blog/:slug" element={<BlogDetail />} />
                  <Route path="/guides/index.html" element={<Blogs />} />
                  <Route path="/guides/:slug" element={<BlogDetail />} />
                  <Route path="/campaigns/varahi-navaratrulu" element={<VarahiNavaratrulu />} />

                  
          {/* Admin Routes */}
          <Route path="/admin" element={<AdminRoute><Navigate to="/admin/dashboard" replace /></AdminRoute>} />
          <Route path="/admin/dashboard" element={<AdminRoute><AdminDashboard /></AdminRoute>} />
          <Route path="/admin/orders" element={<AdminRoute><AdminOrders /></AdminRoute>} />
          <Route path="/admin/orders/:orderId" element={<AdminRoute><AdminOrderDetails /></AdminRoute>} />
          <Route path="/admin/products" element={<AdminRoute><AdminProducts /></AdminRoute>} />
          <Route path="/admin/bestsellers" element={<AdminRoute><AdminBestsellers /></AdminRoute>} />
          <Route path="/admin/blogs" element={<AdminRoute><AdminBlogs /></AdminRoute>} />
          <Route path="/admin/banners" element={<AdminRoute><AdminBanners /></AdminRoute>} />
          <Route path="/admin/reviews" element={<AdminRoute><AdminReviews /></AdminRoute>} />
          <Route path="/admin/coupons" element={<AdminRoute><AdminCoupons /></AdminRoute>} />
          <Route path="/admin/offers" element={<AdminRoute><AdminOffers /></AdminRoute>} />
          <Route path="/admin/bracelet-gift" element={<AdminRoute><AdminBraceletGift /></AdminRoute>} />
          <Route path="/admin/recent-purchases" element={<AdminRoute><AdminRecentPurchases /></AdminRoute>} />
          <Route path="/admin/gemfinder" element={<AdminRoute><AdminGemfinder /></AdminRoute>} />
          <Route path="/admin/app-promotion" element={<AdminRoute><AdminAppPromotion /></AdminRoute>} />
                  
                  {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
                  <Route path="*" element={<NotFound />} />
                  </Routes>
                  </Suspense>
                </RouteErrorBoundary>
              </BrowserRouter>
              </BraceletGiftProvider>
            </CartProvider>
          </WishlistProvider>
        </AddressProvider>
      </AuthProvider>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
